/**
 * Authentication Code Component
 * Komponent för tvåfaktorsautentisering med OTP-kod
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { verifyOtpRequest, resendOtpRequest, type OtpType } from '@/services/auth/authService';
import { useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';

type AuthenticationCodeType = {
  setIsVerified: React.Dispatch<React.SetStateAction<boolean>>;
  email: string;
  type: OtpType;
};

const AuthenticationCode = ({ setIsVerified, email, type }: AuthenticationCodeType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLoginDetails } = useAuthActions();
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const maskedEmail = email?.replace(/(\w{1})(.*)(@.*)/, '$1****$3');

  useEffect(() => {
    const timer = timeLeft > 0 && setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    if (timeLeft === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft]);

  const handleResend = useCallback(async () => {
    try {
      setIsLoading(true);
      setTimeLeft(30);
      setCanResend(false);
      setError('');

      await resendOtpRequest({ email, type });
      toast.success(t('auth.otpResent') || 'Verifieringskod skickad igen');
    } catch (error: any) {
      console.error('OTP resend error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Kunde inte skicka ny kod';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [email, type, t]);

  const onVerifyOTP = useCallback(async () => {
    if (otp.length !== 4) {
      toast.error(t('auth.invalidOtp') || 'Ange en giltig 4-siffrig kod');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await verifyOtpRequest({
        email,
        otp,
        type,
      });

      if (response.data?.success && response.data.data) {
        // Type guard för AuthData
        const authData = response.data.data;
        if ('accessToken' in authData && 'refreshToken' in authData && 'user' in authData) {
          // Spara login details i Zustand store
          setLoginDetails({
            accessToken: authData.accessToken,
            refreshToken: authData.refreshToken,
            user: authData.user,
          });
        }

        toast.success(t('auth.otpVerified') || 'Verifiering lyckades');
        navigate('/');
      }
    } catch (error: any) {
      console.error('OTP verify error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Verifiering misslyckades';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [otp, email, type, navigate, setLoginDetails, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100">
      <div className="max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8">
        <div className="flex flex-col justify-center gap-8">
          <div>
            <h2 className="text-2xl font-bold text-center mb-2 text-gray-800 dark:text-gray-800">
              {t('auth.otpTitle') || 'Verifieringskod'}
            </h2>
            <p className="text-center text-textSecondary dark:text-textSecondary text-sm">
              {t('auth.otpDescription') || 'Vi har skickat en verifieringskod till'} {maskedEmail}
            </p>
          </div>

          <div className="flex flex-col items-center space-y-4 relative">
            <InputOTP
              maxLength={4}
              value={otp}
              onChange={(value) => {
                if (/^\d*$/.test(value)) {
                  setOtp(value);
                  setError('');
                }
              }}
              className="gap-2"
              onComplete={onVerifyOTP}
              disabled={isLoading}
            >
              <InputOTPGroup className="gap-2">
                {[...Array(4)].map((_, index) => (
                  <InputOTPSlot
                    key={index}
                    index={index}
                    className={`border rounded-sm bg-white dark:bg-white text-gray-900 dark:text-gray-900 ${
                      error ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                    } transition-colors`}
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
            {error && (
              <p className="text-red-500 text-sm absolute top-full left-1/2 -translate-x-1/2 mt-2">
                {error}
              </p>
            )}
            <div className="text-textTertiary dark:text-textTertiary flex gap-2 items-center text-sm mt-4">
              <span>{`00:${timeLeft < 10 ? `0${timeLeft}` : timeLeft}`}</span>
              <button
                type="button"
                className="text-blue-600 hover:text-blue-800 disabled:text-textTertiary disabled:cursor-not-allowed"
                disabled={!canResend || isLoading}
                onClick={handleResend}
              >
                {t('auth.resend') || 'Skicka igen'}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsVerified(false)}
              disabled={isLoading}
              className="text-textSecondary dark:text-textSecondary hover:text-gray-800 dark:hover:text-gray-800 text-sm mt-4"
            >
              {t('common.back') || 'Tillbaka'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationCode;

